import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";
const SENT = 'Check your email for a reset link.';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.status === 429) {
        setError('Too many requests. Please wait a minute and try again.');
      } else if (!res.ok) {
        setError('Something went wrong. Please try again.');
      } else {
        setDone(true);
      }
    } catch (err) {
      setError('We could not reach RideBuraq. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-5">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />
      <button
        data-testid="back-btn"
        onClick={() => navigate('/login')}
        className="absolute left-5 top-6 w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white"
        aria-label="Back"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO} alt="RideBuraq" className="w-14 h-14 object-contain mb-5" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Account help</p>
          <h1 className="text-4xl font-semibold tracking-tight">Forgot password?</h1>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          {done ? (
            <div data-testid="forgot-sent" className="text-center space-y-3">
              <p className="text-lg font-medium">{SENT}</p>
              <p className="text-sm text-gray-400">
                If an account exists for that email, the link is on its way. It expires in 30 minutes. Not there? Check your spam folder.
              </p>
              <Link to="/login" className="inline-block mt-2 text-orange-400 hover:text-orange-300 font-medium text-sm">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-400">Enter the email you signed up with and we will send you a link to choose a new password.</p>
              <div>
                <Label htmlFor="email" className="text-gray-400 text-xs tracking-wide uppercase">Email</Label>
                <Input
                  id="email" name="email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl"
                  placeholder="you@example.com"
                />
              </div>
              {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="w-full rounded-full py-3.5 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-60 flex items-center justify-center"
              >
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : 'Send reset link'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
